import { createContext, useContext, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import db from '../firebase/firebaseConfig';
import { useSite } from './SiteContext';

const SITE_NUMBERS_COLLECTION = 'siteNumbers';

const RedirectContext = createContext({ redirectActive: false });

/** Absolute URL for redirect target (path or full URL string). */
function resolveRedirectHref(raw) {
  const t = (raw || '').trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.startsWith('/') ? t : `/${t}`;
  return new URL(path, window.location.origin).href;
}

/** Avoid infinite reload when redirect target is the current page. */
function isAlreadyAtTarget(absoluteHref) {
  try {
    const target = new URL(absoluteHref);
    const current = new URL(window.location.href);
    if (target.origin !== current.origin) return false;
    return target.pathname === current.pathname;
  } catch {
    return false;
  }
}

/**
 * `redirectEnabled` (boolean) + `redirectUrl` from Firestore → immediate full redirect.
 * Falls back to legacy `popup: true` if `redirectEnabled` is absent (migration).
 */
export const RedirectProvider = ({ children }) => {
  const { firebaseSiteDocId } = useSite();

  useEffect(() => {
    if (!firebaseSiteDocId) return undefined;

    const docRef = doc(db, SITE_NUMBERS_COLLECTION, firebaseSiteDocId);
    const unsub = onSnapshot(
      docRef,
      (snap) => {
        if (!snap.exists()) return;

        const data = snap.data() || {};
        const hasNewFlag = Object.prototype.hasOwnProperty.call(
          data,
          'redirectEnabled'
        );
        const enabled = hasNewFlag
          ? data.redirectEnabled === true
          : data.popup === true;

        const rawUrl = (data.redirectUrl || '').trim();
        if (!enabled || !rawUrl) return;

        const href = resolveRedirectHref(rawUrl);
        if (!href) return;

        if (isAlreadyAtTarget(href)) return;

        window.location.replace(href);
      },
      (err) => {
        console.error(
          `[Redirect] siteNumbers/${firebaseSiteDocId}:`,
          err
        );
      }
    );

    return () => unsub();
  }, [firebaseSiteDocId]);

  return (
    <RedirectContext.Provider value={{ redirectActive: true }}>
      {children}
    </RedirectContext.Provider>
  );
};

export const useRedirect = () => useContext(RedirectContext);

export default RedirectContext;
